// Copyright 2016 Sorama B.V.

import { autoinject, BindingEngine, Disposable, transient } from "aurelia-framework";
import { BindingSignaler } from "aurelia-templating-resources";
import { IWorkflowModuleCatalog } from "catalogs/IWorkflowModuleCatalog";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import {
    IWorkflowModuleConfig,
    WorkflowModuleType,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { IWorkflowModuleController } from "libraries/WorkflowFramework/Interfaces/IWorkflowRouter";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import {
    ChannelSelectionModalReturnState,
    ChannelSelectionModalState,
} from "modules/modals/ChannelSelection/ChannelSelectionModal";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";
import { ChannelSelectionUsage } from "services/ChannelSelection/ChannelSelectionUsage";
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";

class StreamableModule {
    moduleConfig: IWorkflowModuleConfig;
    moduleInstance?: IWorkflowModuleController;
}

/**
 * MODULE OPERATION DESCRIPTION - StreamerModule
 * The streamer field module is a control module that controls all streamer related settings
 * The viewmodel controls the selected devices and active stream measurements via the related IReservableHardwareService and IStreamMeasurementServices
 * Other properties that are controlled in this module are (workflowproperty or router paths):
 * - Framerate
 * - Sample rate
 * - Visibility of near field / far field / waterfall workmodules
 */
@transient()
@autoinject
export class StreamerModule implements IWorkflowModuleLifetime {
    //#region Fields
    private disposables: Disposable[] = [];

    private streamActive: boolean;
    private streamerModules: StreamableModule[] = [];
    private moduleInstance: IWorkflowModuleInstance;

    //#endregion

    //#region Workflow property
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public channelSelection: ChannelSelection;
    //#endregion

    constructor(
        private readonly measurementAcquisitionService: IMeasurementAcquisitionService,
        private readonly bindingEngine: BindingEngine,
        private readonly workflowCatalog: IWorkflowModuleCatalog,
        private readonly signaler: BindingSignaler
    ) {}

    //#region Lifecycle functions

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        const streamerModules = this.workflowCatalog.getWorkflowModules(WorkflowModuleType.Streaming);
        const activeStreamerModules = this.moduleInstance
            .getWorkflow()
            .getModules()
            .filter((x) => x.config.moduleType == WorkflowModuleType.Streaming);

        for (const mod of streamerModules) {
            const newMod = new StreamableModule();
            newMod.moduleConfig = mod;
            const activeMod = activeStreamerModules.find((x) => x.config.modulePath == mod.modulePath);
            if (activeMod) newMod.moduleInstance = activeMod;

            this.streamerModules.push(newMod);
        }

        this.disposables.push(
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "channelSelection")
                .subscribe((newValue) => {
                    this.deviceChannelSelectionChanged(newValue);
                }),
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "activeSetup")
                .subscribe((newValue) => this.signaler.signal("setup-changed"))
        );

        if (
            this.measurementAcquisitionService.activeSetup &&
            this.measurementAcquisitionService.activeSetup.length === 1
        ) {
            const channelSelections: { [id: string]: ChannelSelectionUsage } = {};
            for (const device of this.measurementAcquisitionService.activeSetup) {
                channelSelections[device.id] = ChannelSelectionUsage.LiveStreaming;
            }

            this.measurementAcquisitionService.setChannelSelections(channelSelections);
        }

        this.updateChannelSelection(this.channelSelection);

        this.moduleInstance.getWorkflow().subscribeModulesChanged(() => {
            this.modulesChanged();
        });
    }

    public deactivateModule(): void {
        if (this.measurementAcquisitionService.isAcquiring.statusCode)
            this.measurementAcquisitionService.stopAcquisition();

        while (this.disposables.length > 0) this.disposables.pop().dispose();
    }

    //#endregion

    //#region Change callbacks

    private deviceChannelSelectionChanged = (newValue: ChannelSelection) => {
        this.channelSelection = newValue;
    };

    //#endregion

    //#region Control

    private start(): Promise<void> {
        if (!this.measurementAcquisitionService.isReserved.available) return;

        // Start acquisition
        this.measurementAcquisitionService.setUseReferenceMicrophone(false);
        this.measurementAcquisitionService.startAcquisition();
        this.streamActive = true;
    }

    private stop(): Promise<void> {
        if (!this.measurementAcquisitionService.isAcquiring.statusCode) return;

        this.measurementAcquisitionService.stopAcquisition();
        this.streamActive = false;
    }

    //#endregion

    //#region View related functions

    public closeModule(): void {
        const closeAction = () => {
            // Remove dependent modules
            for (const mod of this.streamerModules) {
                if (mod.moduleInstance) mod.moduleInstance.close();
            }
        };

        if (this.measurementAcquisitionService.isAcquiring.statusCode) {
            this.moduleInstance
                .createNotification()
                .withMessage("Closing this window will also stop the measurement. Are you sure?")
                .withButton("Yes", closeAction)
                .withButton("No")
                .show();
        } else {
            closeAction();
        }
    }

    private modulesChanged() {
        const activeStreamerModules = this.moduleInstance
            .getWorkflow()
            .getModules()
            .filter((x) => x.config.moduleType == WorkflowModuleType.Streaming);

        for (const mod of this.streamerModules) {
            mod.moduleInstance = activeStreamerModules.find((x) => x.config.modulePath == mod.moduleConfig.modulePath);
        }
    }

    private showChannelSelectionModal(): void {
        if (
            !this.measurementAcquisitionService.activeSetup ||
            this.measurementAcquisitionService.activeSetup.length !== 1
        )
            return;

        this.moduleInstance
            .createModal<ChannelSelectionModalState, ChannelSelectionModalReturnState>({
                ...WorkflowModalCatalog.ChannelSelectionModal,
                smallModalSize: this.measurementAcquisitionService.activeSetup.sensorPositions.length <= 64,
            })
            .withState({ frontEnd: this.measurementAcquisitionService.activeSetup[0].measurementDevice.frontEnd })
            .whenClosed((x) => {
                this.updateChannelSelection(x.selectedSelection);
            })
            .show();
    }

    private updateChannelSelection(selection: ChannelSelection) {
        this.channelSelection = selection;

        if (
            this.channelSelection &&
            this.measurementAcquisitionService.activeSetup &&
            this.measurementAcquisitionService.activeSetup.length === 1
        ) {
            const channelSelections: { [id: string]: ChannelSelection } = {};
            for (const device of this.measurementAcquisitionService.activeSetup) {
                channelSelections[device.id] = this.channelSelection;
            }

            this.measurementAcquisitionService.setChannelSelections(channelSelections);
        }
    }

    private toggleModule(streamerModule: StreamableModule): boolean {
        if (streamerModule.moduleInstance) {
            streamerModule.moduleInstance.close();
            streamerModule.moduleInstance = undefined;
        } else {
            this.moduleInstance
                .getWorkflow()
                .addModule(streamerModule.moduleConfig)
                .then((x) => {
                    streamerModule.moduleInstance = x;
                });
        }

        // Return true to make the event update the buttons state
        return true;
    }

    private showModuleButton(module: StreamableModule): boolean {
        // Near field currently doesn't work in streaming mode due to problems with microphone ordering
        const usingMultiDeviceSetup = this.measurementAcquisitionService.activeSetup?.length > 1;
        if (module.moduleConfig.name == "nearfield" && usingMultiDeviceSetup) {
            return false;
        }
        return true;
    }
    //#endregion
}
